const chapter = {
  id: 'metallurgy',
  slug: 'metallurgy',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'General Principles & Processes of Isolation of Elements',
        hindi: 'तत्वों के निष्कर्षण के सामान्य सिद्धान्त',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'occurrence',
      title: {
        english: 'Occurrence — Minerals & Ores',
        hindi: 'प्राकृतिक अवस्था — खनिज और अयस्क',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Mineral**: naturally occurring chemical substance containing the metal. **Ore**: a mineral from which metal can be **economically** extracted.',
        hindi: '**खनिज**: प्राकृतिक रूप से पाया जाने वाला रासायनिक पदार्थ जिसमें धातु होती है। **अयस्क**: एक खनिज जिससे धातु **आर्थिक रूप से** निकाली जा सकती है।',
            },
            {
              english: 'Classification of ores:',
        hindi: 'अयस्कों का वर्गीकरण:',
            },
            {
              english: '• **Native** — pure (Au, Ag, Pt, Cu).',
        hindi: '• **मूल** - शुद्ध (Au, Ag, Pt, Cu)।',
            },
            {
              english: '• **Oxide** — Hematite Fe₂O₃, Magnetite Fe₃O₄, Bauxite Al₂O₃·2H₂O, Cassiterite SnO₂, Cuprite Cu₂O.',
        hindi: '• **ऑक्साइड** - हेमेटाइट Fe₂O₃, मैग्नेटाइट Fe₃O₄, बॉक्साइट Al₂O₃·2H₂O, कैसिटेराइट SnO₂, Cuprite Cu₂O।',
            },
            {
              english: '• **Sulphide** — Galena PbS, Zinc blende ZnS, Copper pyrites CuFeS₂, Cinnabar HgS, Argentite Ag₂S.',
        hindi: '• **सल्फाइड** - गैलेना पीबीएस, जिंक ब्लेंड ZnS, कॉपर पाइराइट्स CuFeS₂, सिनेबार HgS, अर्जेंटाइट Ag₂S।',
            },
            {
              english: '• **Halide** — Rock salt NaCl, Sylvine KCl, Cryolite Na₃AlF₆, Horn silver AgCl.',
        hindi: '• **हैलाइड** - सेंधा नमक NaCl, सिल्विन KCl, क्रायोलाइट Na₃AlF₆, हॉर्न सिल्वर AgCl।',
            },
            {
              english: '• **Carbonate** — Calamine ZnCO₃, Siderite FeCO₃, Limestone CaCO₃, Magnesite MgCO₃, Malachite Cu(OH)₂·CuCO₃.',
        hindi: '• **कार्बोनेट** - कैलामाइन ZnCO₃, साइडराइट FeCO₃, चूना पत्थर CaCO₃, मैग्नेसाइट MgCO₃, मैलाकाइट Cu(OH)₂·CuCO₃।',
            },
            {
              english: '• **Sulphate** — Gypsum CaSO₄·2H₂O, Epsom MgSO₄·7H₂O, Barytes BaSO₄.',
        hindi: '• **सल्फेट** - जिप्सम CaSO₄·2H₂O, एप्सम MgSO₄·7H₂O, बैराइट्स BaSO₄।',
            },
            {
              english: '• **Phosphate / silicate** — Apatite Ca₅(PO₄)₃X (X = F, Cl, OH); Asbestos.',
        hindi: '• **फॉस्फेट / सिलिकेट** - एपेटाइट Ca₅(PO₄)₃X (X = F, Cl, OH); अभ्रक.',
            },
          ],
        },
      ],
    },
    {
      id: 'concentration',
      title: {
        english: 'Concentration (Dressing) of Ore',
        hindi: 'अयस्क का सान्द्रण (प्रसाधन)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Hydraulic washing / gravity separation** — for heavy oxide ores (hematite, cassiterite); ore washed in upward stream of water; lighter gangue washed away.',
        hindi: '**हाइड्रोलिक धुलाई/गुरुत्वाकर्षण पृथक्करण** - भारी ऑक्साइड अयस्कों (हेमेटाइट, कैसिटेराइट) के लिए; पानी की ऊपरी धारा में धोया गया अयस्क; लाइटर गैंग बह गया।',
            },
            {
              english: '**Magnetic separation** — for ferromagnetic gangue/ore (e.g. magnetite Fe₃O₄, wolframite from cassiterite).',
        hindi: '**चुंबकीय पृथक्करण** - लौहचुंबकीय gangue/ore के लिए (उदाहरण के लिए मैग्नेटाइट Fe₃O₄, कैसिटेराइट से वोल्फ्रामाइट)।',
            },
            {
              english: '**Froth flotation** — for sulphide ores (galena, zinc blende, copper pyrites). Crushed ore + water + pine oil/eucalyptus oil + frothing agent + collector (xanthates). Air blown in → sulphide grains stick to froth (hydrophobic), gangue settles. **Depressants**: NaCN suppresses ZnS while floating PbS in galena-blende mixture.',
        hindi: '**झाग प्लवन** - सल्फाइड अयस्कों (गैलेना, जिंक ब्लेंड, कॉपर पाइराइट्स) के लिए। कुचला हुआ अयस्क + पानी + पाइन oil/eucalyptus तेल + झाग बनाने वाला एजेंट + संग्राहक (जैंथेट्स)। हवा अंदर चली जाती है → सल्फाइड के कण झाग (हाइड्रोफोबिक) से चिपक जाते हैं, गैंग जम जाता है। **अवसादक**: गैलेना-मिश्रण मिश्रण में PbS तैरते समय NaCN ZnS को दबा देता है।',
            },
            {
              english: '**Leaching (chemical)** — selectively dissolves the ore:',
        hindi: '**लीचिंग (रासायनिक)** - अयस्क को चुनिंदा रूप से घोलता है:',
            },
            {
              english: '• Bauxite leached with NaOH (Bayer): $Al_2O_3 + 2NaOH + 3H_2O \\to 2Na[Al(OH)_4]$. Then $Na[Al(OH)_4] + CO_2 \\to NaHCO_3 + Al(OH)_3$; $Al(OH)_3 \\xrightarrow{\\Delta} Al_2O_3$.',
        hindi: '• NaOH (बायर) के साथ निक्षालित बॉक्साइट: $Al_2O_3 + 2NaOH + 3H_2O \\to 2Na[Al(OH)_4]$। फिर $Na[Al(OH)_4] + CO_2 \\to NaHCO_3 + Al(OH)_3$; $Al(OH)_3 \\xrightarrow{\\Delta} Al_2O_3$.',
            },
            {
              english: '• Silver / Gold leached with dilute NaCN in air (cyanide / Mac Arthur–Forrest): $4M + 8NaCN + O_2 + 2H_2O \\to 4Na[M(CN)_2] + 4NaOH$; recovered by Zn dust: $2Na[Au(CN)_2] + Zn \\to Na_2[Zn(CN)_4] + 2Au$.',
        hindi: '• चांदी/सोना हवा में पतला NaCN के साथ निक्षालित (सायनाइड/मैक आर्थर-फॉरेस्ट): $4M + 8NaCN + O_2 + 2H_2O \\to 4Na[M(CN)_2] + 4NaOH$; Zn धूल द्वारा पुनर्प्राप्त: $2Na[Au(CN)_2] + Zn \\to Na_2[Zn(CN)_4] + 2Au$।',
            },
          ],
        },
      ],
    },
    {
      id: 'reduction',
      title: {
        english: 'Conversion to Oxide & Reduction to Metal',
        hindi: 'ऑक्साइड में रूपान्तरण और धातु में अवकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: `**Calcination** — heating in **absence / limited supply** of air. Removes water, CO₂; converts carbonates / hydroxides to oxide.
  $CaCO_3 \\to CaO + CO_2$;  $Al_2O_3\\cdot 2H_2O \\to Al_2O_3 + 2H_2O$.`,
              hindi: '**कैल्सीनेशन** — हवा की **अनुपस्थिति/सीमित आपूर्ति** में ताप। पानी निकालता है, CO₂; कार्बोनेट/हाइड्रॉक्साइड को ऑक्साइड में परिवर्तित करता है। $CaCO_3 \\to CaO + CO_2$; $Al_2O_3\\cdot 2H_2O \\to Al_2O_3 + 2H_2O$.',
            },
            {
              english: '**Roasting** — heating in **excess of air**. Used for sulphide ores. $2ZnS + 3O_2 \\to 2ZnO + 2SO_2$; $2PbS + 3O_2 \\to 2PbO + 2SO_2$.',
        hindi: '**भूनना** — **हवा की अधिकता** में गर्म करना। सल्फाइड अयस्कों के लिए उपयोग किया जाता है। $2ZnS + 3O_2 \\to 2ZnO + 2SO_2$; $2PbS + 3O_2 \\to 2PbO + 2SO_2$.',
            },
            {
              english: '**Reduction** of oxide to metal:',
        hindi: '**धातु में ऑक्साइड का अपचयन**:',
            },
            {
              english: '• **Smelting (carbon reduction)** — cheap; for Fe, Zn, Sn, Pb. $ZnO + C \\to Zn + CO$.',
        hindi: '• **प्रगलन (कार्बन कटौती)** — सस्ता; Fe, Zn, Sn, Pb के लिए। $ZnO + C \\to Zn + CO$.',
            },
            {
              english: '• **Self-reduction (auto-reduction)** — for Hg, Cu (sulphide partly roasted then heated in absence of air): $2Cu_2S + 3O_2 \\to 2Cu_2O + 2SO_2$ then $2Cu_2O + Cu_2S \\to 6Cu + SO_2$.',
        hindi: '• **सेल्फ-रिडक्शन (ऑटो-रिडक्शन)** - Hg, Cu के लिए (सल्फाइड को आंशिक रूप से भुना जाता है और फिर हवा की अनुपस्थिति में गर्म किया जाता है): $2Cu_2S + 3O_2 \\to 2Cu_2O + 2SO_2$ फिर $2Cu_2O + Cu_2S \\to 6Cu + SO_2$।',
            },
            {
              english: '• **Aluminothermic / Goldschmidt** — Cr, Mn from their oxides using Al: $Cr_2O_3 + 2Al \\to Al_2O_3 + 2Cr$.',
        hindi: '• **एल्युमिनोथर्मिक / गोल्डस्मिड्ट** - Al: $Cr_2O_3 + 2Al \\to Al_2O_3 + 2Cr$ का उपयोग करके उनके ऑक्साइड से Cr, Mn।',
            },
            {
              english: '• **Hydrogen reduction** — for Mo, W: $WO_3 + 3H_2 \\to W + 3H_2O$.',
        hindi: '• **हाइड्रोजन कमी** - मो, डब्ल्यू के लिए: $WO_3 + 3H_2 \\to W + 3H_2O$।',
            },
            {
              english: '• **Electrolytic reduction** — for highly electropositive metals (Na, Mg, Al, Ca) from molten salts.',
        hindi: '• **इलेक्ट्रोलाइटिक कमी** - पिघले हुए लवणों से अत्यधिक इलेक्ट्रोपोसिटिव धातुओं (Na, Mg, Al, Ca) के लिए।',
            },
          ],
        },
      ],
    },
    {
      id: 'ellingham',
      title: {
        english: 'Ellingham Diagram & Thermodynamic Principle',
        hindi: 'एलिंघम आरेख और ऊष्मागतिकी सिद्धान्त',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Plot of standard Gibbs free energy of formation $\\Delta_f G^\\circ$ of metal oxides (per mole O₂) versus temperature. Reduction $M_xO_y + \\text{red} \\to M + \\text{red-O}$ is feasible if it has $\\Delta G < 0$, i.e. the reductant\'s line lies **below** the metal-oxide line at that temperature.',
        hindi: 'धातु ऑक्साइड (प्रति मोल O₂) बनाम तापमान के निर्माण की मानक गिब्स मुक्त ऊर्जा $\\Delta_f G^\\circ$ का प्लॉट। कटौती $M_xO_y + \\text{red} \\to M + \\text{red-O}$ संभव है यदि इसमें $\\Delta G < 0$ है, अर्थात उस तापमान पर रिडक्टेंट की रेखा धातु-ऑक्साइड रेखा के **नीचे** होती है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Most lines slope **upwards** (entropy of the system $\\downarrow$ as O₂ consumed). Exception: $C \\to CO$ slopes **downwards** (gas formation increases entropy) — so above ~983 K, carbon can reduce ZnO; CO can reduce iron oxide (blast furnace).',
        hindi: 'अधिकांश लाइनों का ढलान **ऊपर की ओर** होता है (O₂ के उपभोग के अनुसार सिस्टम की एन्ट्रापी $\\downarrow$)। अपवाद: $C \\to CO$ ढलान **नीचे की ओर** (गैस बनने से एन्ट्रापी बढ़ती है) - इसलिए ~983 K से ऊपर, कार्बन ZnO को कम कर सकता है; CO आयरन ऑक्साइड (ब्लास्ट फर्नेस) को कम कर सकता है।',
            },
            {
              english: 'Cross-over points indicate temperature thresholds for feasibility.',
        hindi: 'क्रॉस-ओवर पॉइंट व्यवहार्यता के लिए तापमान सीमा का संकेत देते हैं।',
            },
            {
              english: 'Limitations: kinetic factors not addressed; some metals (Al) form stable carbides instead of metal at high $T$.',
        hindi: 'सीमाएं: गतिज कारकों पर ध्यान नहीं दिया गया; कुछ धातुएँ (Al) उच्च $T$ पर धातु के बजाय स्थिर कार्बाइड बनाती हैं। **आसवन** - कम उबलने वाली धातुओं (Zn, Hg) के लिए। **द्रवीकरण** - ढलान वाले चूल्हे पर कम पिघलने वाली धातुओं के लिए (Sn, Pb)।',
            },
          ],
        },
      ],
    },
    {
      id: 'refining',
      title: {
        english: 'Refining of Crude Metal',
        hindi: 'कच्ची धातु का परिष्करण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Distillation** — for low-boiling metals (Zn, Hg).',
        hindi: '**आसवन** - कम उबलने वाली धातुओं (Zn, Hg) के लिए।',
            },
            {
              english: '**Liquation** — for low-melting metals on a sloping hearth (Sn, Pb).',
        hindi: '**द्रवीकरण** - ढलान वाले चूल्हे पर कम पिघलने वाली धातुओं के लिए (एसएन, पीबी)।',
            },
            {
              english: '**Poling** — stirring molten impure metal with green wood; reduces metal oxides (Cu, Sn).',
        hindi: '**पोलिंग** - हरी लकड़ी के साथ पिघली हुई अशुद्ध धातु को हिलाना; धातु ऑक्साइड (Cu, Sn) को कम करता है।',
            },
            {
              english: '**Electrolytic refining** — most common for Cu, Ag, Au, Al, Zn, Ni. Impure metal as anode, pure metal as cathode in salt solution. Pure metal deposits at cathode; noble impurities settle as **anode mud**.',
        hindi: '**इलेक्ट्रोलाइटिक रिफाइनिंग** - Cu, Ag, Au, Al, Zn, Ni के लिए सबसे आम। नमक के घोल में अशुद्ध धातु एनोड के रूप में, शुद्ध धातु कैथोड के रूप में। कैथोड पर शुद्ध धातु का जमाव; उत्कृष्ट अशुद्धियाँ **एनोड कीचड़** के रूप में बस जाती हैं।',
            },
            {
              english: '**Zone refining** — for ultra-pure semiconductors (Si, Ge, Ga, In, Sb). Molten zone moved through a rod; impurities concentrate in the molten zone and are swept to one end.',
        hindi: '**जोन शोधन** - अति-शुद्ध अर्धचालकों (Si, Ge, Ga, In, Sb) के लिए। पिघला हुआ क्षेत्र एक छड़ के माध्यम से चला गया; अशुद्धियाँ पिघले हुए क्षेत्र में केंद्रित होती हैं और एक छोर तक बह जाती हैं।',
            },
            {
              english: '**Vapour-phase refining**:',
        hindi: '**वाष्प-चरण शोधन**:',
            },
            {
              english: '• **Mond process** for Ni: $Ni + 4CO \\xrightarrow{330\\,K} [Ni(CO)_4] \\xrightarrow{450\\text{–}470\\,K} Ni + 4CO$.',
        hindi: '• नी के लिए **मोंड प्रक्रिया**: $Ni + 4CO \\xrightarrow{330\\,K} [Ni(CO)_4] \\xrightarrow{450\\text{–}470\\,K} Ni + 4CO$।',
            },
            {
              english: '• **Van Arkel process** for Ti, Zr: $Ti + 2I_2 \\to TiI_4$ (low $T$) then $TiI_4 \\xrightarrow{1700\\,K, W\\text{ filament}} Ti + 2I_2$.',
        hindi: '• **वैन आर्केल प्रक्रिया** Ti, Zr के लिए: $Ti + 2I_2 \\to TiI_4$ (कम $T$) फिर $TiI_4 \\xrightarrow{1700\\,K, W\\text{ filament}} Ti + 2I_2$।',
            },
            {
              english: '**Chromatography** — for trace separation of components on differential adsorption.',
        hindi: '**क्रोमैटोग्राफी** - विभेदक सोखना पर घटकों के ट्रेस पृथक्करण के लिए।',
            },
          ],
        },
      ],
    },
    {
      id: 'specific-extractions',
      title: {
        english: 'Important Industrial Extractions',
        hindi: 'महत्वपूर्ण औद्योगिक निष्कर्षण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Iron (blast furnace)** — Fe₂O₃ + coke + limestone. Reactions in zones (top → bottom): $Fe_2O_3 + 3CO \\to 2Fe + 3CO_2$; $CaCO_3 \\to CaO + CO_2$; $CaO + SiO_2 \\to CaSiO_3$ (slag); molten Fe at bottom (pig iron, ~4 % C).',
        hindi: '**लोहा (ब्लास्ट फर्नेस)** — Fe₂O₃ + कोक + चूना पत्थर। ज़ोन में प्रतिक्रियाएं (ऊपर → नीचे): $Fe_2O_3 + 3CO \\to 2Fe + 3CO_2$; $CaCO_3 \\to CaO + CO_2$; $CaO + SiO_2 \\to CaSiO_3$ (स्लैग); तल पर पिघला हुआ Fe (पिग आयरन, ~4% C)।',
            },
            {
              english: '**Aluminium (Hall–Héroult)** — molten Al₂O₃ in cryolite (Na₃AlF₆) + CaF₂ at ~1270 K with carbon electrodes. Cathode: $Al^{3+} + 3e^- \\to Al$; Anode: $C + O^{2-} \\to CO/CO_2 + e^-$ (anodes consumed).',
        hindi: '**एल्यूमीनियम (हॉल-हेरोल्ट)** - कार्बन इलेक्ट्रोड के साथ ~1270 K पर क्रायोलाइट (Na₃AlF₆) + CaF₂ में पिघला हुआ Al₂O₃। कैथोड: $Al^{3+} + 3e^- \\to Al$; एनोड: $C + O^{2-} \\to CO/CO_2 + e^-$ (एनोड खपत)।',
            },
            {
              english: '**Copper** — sulphide ore concentrated by froth flotation; partially roasted; then **bessemerisation** in silica-lined converter: $2Cu_2S + 3O_2 \\to 2Cu_2O + 2SO_2$; $2Cu_2O + Cu_2S \\to 6Cu + SO_2$ (blister copper). Refined electrolytically.',
        hindi: '**तांबा** - झाग उत्प्लावन द्वारा सांद्रित सल्फाइड अयस्क; आंशिक रूप से भुना हुआ; फिर सिलिका-लाइन वाले कनवर्टर में **बेसेमेराइजेशन**: $2Cu_2S + 3O_2 \\to 2Cu_2O + 2SO_2$; $2Cu_2O + Cu_2S \\to 6Cu + SO_2$ (ब्लिस्टर कॉपर)। इलेक्ट्रोलाइटिक रूप से परिष्कृत।',
            },
            {
              english: '**Zinc** — ZnS roasted to ZnO; reduced by C: $ZnO + C \\to Zn + CO$; further refined by distillation / electrolysis.',
        hindi: '**जस्ता** - ZnS को ZnO में भूनकर; C द्वारा घटाया गया: $ZnO + C \\to Zn + CO$; आसवन/इलेक्ट्रोलिसिस द्वारा और अधिक परिष्कृत किया गया।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'Al_2O_3 + 2NaOH + 3H_2O \\to 2Na[Al(OH)_4]',
      desc: {
        english: 'Bayer leaching',
        hindi: 'बायर लीचिंग',
      },
    },
    {
      latex: 'Cr_2O_3 + 2Al \\to Al_2O_3 + 2Cr',
      desc: {
        english: 'Aluminothermic (Goldschmidt)',
        hindi: 'एल्युमिनोथर्मिक (गोल्डश्मिट)',
      },
    },
    {
      latex: '2Cu_2O + Cu_2S \\to 6Cu + SO_2',
      desc: {
        english: 'Self-reduction in copper extraction',
        hindi: 'तांबे के निष्कर्षण में स्व-कमी',
      },
    },
    {
      latex: 'Fe_2O_3 + 3CO \\to 2Fe + 3CO_2',
      desc: {
        english: 'Blast furnace',
        hindi: 'वात भट्टी',
      },
    },
    {
      latex: 'Ni + 4CO \\xrightleftharpoons[450\\,K]{330\\,K} [Ni(CO)_4]',
      desc: {
        english: 'Mond process',
        hindi: 'मोंड प्रक्रिया',
      },
    },
    {
      latex: 'Ti + 2I_2 \\xrightleftharpoons[1700\\,K]{500\\,K} TiI_4',
      desc: {
        english: 'Van Arkel',
        hindi: 'वैन आर्केल',
      },
    },
    {
      latex: '4Au + 8NaCN + O_2 + 2H_2O \\to 4Na[Au(CN)_2] + 4NaOH',
      desc: {
        english: 'Cyanide leaching',
        hindi: 'साइनाइड लीचिंग',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Refining',
      problem: {
        english: 'Mond\'s process is used for purification of:',
        hindi: 'मॉन्ड की प्रक्रिया का उपयोग शुद्धिकरण के लिए किया जाता है:',
      },
      answer: {
        english: 'Nickel',
        hindi: 'निकेल',
      },
      solution: {
        english: 'Forms volatile Ni(CO)₄.',
        hindi: 'अस्थिर Ni(CO)₄ बनाता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Concentration',
      problem: {
        english: 'Froth-flotation method is best suited for:',
        hindi: 'फेन-फ्लोटेशन विधि इसके लिए सबसे उपयुक्त है:',
      },
      answer: {
        english: 'Sulphide ores',
        hindi: 'सल्फाइड अयस्क',
      },
      solution: {
        english: 'Hydrophobic sulphide grains float with froth.',
        hindi: 'हाइड्रोफोबिक सल्फाइड कण झाग के साथ तैरते हैं।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Ellingham',
      problem: {
        english: 'In Ellingham diagram, downward sloping line of $C \\to CO$ implies:',
        hindi: 'एलिंघम आरेख में, $C \\to CO$ की नीचे की ओर झुकी हुई रेखा का तात्पर्य है:',
      },
      answer: {
        english: 'Carbon becomes a stronger reducing agent at high $T$',
        hindi: 'उच्च $T$ पर कार्बन एक मजबूत अपचायक बन जाता है',
      },
      solution: {
        english: 'Entropy increases (gas formed).',
        hindi: 'एन्ट्रॉपी बढ़ती है (गैस बनती है)।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Aluminium',
      problem: {
        english: 'Cryolite is added in Hall–Héroult to:',
        hindi: 'क्रायोलाइट को हॉल-हेरोल्ट में जोड़ा जाता है:',
      },
      answer: {
        english: 'Lower fusion temperature of $Al_2O_3$ and increase conductivity',
        hindi: '$Al_2O_3$ का कम संलयन तापमान और चालकता में वृद्धि',
      },
      solution: {
        english: 'Pure Al₂O₃ melts ~2300 K; with cryolite ~1270 K.',
        hindi: 'शुद्ध Al₂O₃ पिघलता है ~2300 K; क्रायोलाइट ~1270 K के साथ।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Calcination/Roasting',
      problem: {
        english: 'Roasting of zinc blende is best represented by:',
        hindi: 'जिंक मिश्रण को भूनने का सबसे अच्छा प्रतिनिधित्व निम्न द्वारा किया जाता है:',
      },
      answer: {
        english: '$2ZnS + 3O_2 \\to 2ZnO + 2SO_2$',
        hindi: '$2ZnS + 3O_2 \\to 2ZnO + 2SO_2$',
      },
      solution: {
        english: 'Heating in excess air.',
        hindi: 'अतिरिक्त हवा में ताप।',
      },
    },
  ],
  next: {
    slug: 'p-block-15',
    title: 'p-Block — Group 15 (Nitrogen Family)',
  },
};

export default chapter;
